import { inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { DataService } from '@core/services';
import { RewardFormat } from '@shared/constants';
import { LoyaltyChallengeConfig } from '@shared/models';

export interface AmountParts {
  currency: string;
  integer: string;
  decimal: string;
  fraction: string;
}

@Pipe({
  name: 'formatAmount',
})
export class AmountPipe implements PipeTransform {
  private readonly locale: string = inject(LOCALE_ID);
  private readonly dataService: DataService = inject(DataService);

  private readonly config: LoyaltyChallengeConfig =
    this.dataService.config() as LoyaltyChallengeConfig;

  transform(value: number, style?: string, nbDigits = 2): string {
    const currency: string = this.config.currency;

    let formatConf: Intl.NumberFormatOptions = {
      minimumFractionDigits: nbDigits,
      maximumFractionDigits: nbDigits,
    };
    if (style === 'currency') {
      formatConf = {
        ...formatConf,
        currency: currency,
        style: 'currency',
      };
    }
    const nf: Intl.NumberFormat = new Intl.NumberFormat(
      this.locale,
      formatConf
    );
    return nf.format(value);
  }
}

@Pipe({ name: 'formatReward' })
export class RewardPipe implements PipeTransform {
  private readonly amountPipe: AmountPipe = inject(AmountPipe);
  private readonly dataService: DataService = inject(DataService);

  private readonly config: LoyaltyChallengeConfig =
    this.dataService.config() as LoyaltyChallengeConfig;

  transform(value: number, imgWidth = '15px', onlyReward = false): string {
    const rewardFormat: RewardFormat = this.config.rewardFormat;
    const rewardTemplate: string = this.config.rewardTemplate as string;

    if (rewardFormat === RewardFormat.PRICE) {
      return this.amountPipe.transform(value, 'currency').replace(/\s/g, '');
    } else if (rewardFormat === RewardFormat.POINTS) {
      const tmpRewardTpl: string = onlyReward ? '{points}' : rewardTemplate;
      const result: string = tmpRewardTpl
        .replace('{imgWidth}', imgWidth)
        .replace('{points}', `${~~value}`.trim());

      return result;
    }
    return '';
  }
}

@Pipe({ name: 'formatAmountParts' })
export class AmountPartsPipe implements PipeTransform {
  private readonly locale: string = inject(LOCALE_ID);
  private readonly dataService: DataService = inject(DataService);

  private readonly config: LoyaltyChallengeConfig =
    this.dataService.config() as LoyaltyChallengeConfig;

  transform(value: number | null | undefined, nbDigits = 2): AmountParts {
    if (value == null || Number.isNaN(value)) {
      return {
        currency: '',
        integer: '',
        decimal: '',
        fraction: '',
      };
    }

    const parts = new Intl.NumberFormat(this.locale, {
      currency: this.config.currency,
      maximumFractionDigits: nbDigits,
      minimumFractionDigits: nbDigits,
      style: 'currency',
    }).formatToParts(value);

    const amountParts: AmountParts = {
      currency: '',
      integer: '',
      decimal: '',
      fraction: '',
    };

    for (const part of parts) {
      if (part.type === 'currency') {
        amountParts.currency = part.value;
      } else if (part.type === 'integer' || part.type === 'group') {
        amountParts.integer = `${amountParts.integer}${part.value}`;
      } else if (part.type === 'decimal') {
        amountParts.decimal = part.value;
      } else if (part.type === 'fraction') {
        amountParts.fraction = part.value;
      }
    }

    return amountParts;
  }
}
